import { useEffect, useState, useRef, useCallback } from 'react';
import useSWRInfinite from 'swr/infinite';
import ProductModuleListingCard from '../ProductModuleListingCard/ProductModuleListingCard';
import { IToolData } from '../../../../../types/products';
import { getToolsListing } from '../../../../../services/toolsModule';
import { ICountryDetail } from '../../../../../types/countries';
import ProductsListCardSkeletonLoader from '../ProductsListCardSkeletonLoader/ProductsListCardSkeletonLoader';
import useDebounce from '../../../../../hooks/useDebounce';
import useFiltersState from '../../../../../state/useFiltersState';
import ProductDetailModal from '../ProductDetailModal/ProductDetailModal';
import { LIST_PRODUCTS_TYPE } from '../../../../../types/orders';
import './ProductsStockListing.scss';

const ProductsStockListing = ({
  searchText,
  listOfCountries
}: {
  searchText: string;
  listOfCountries: ICountryDetail[];
}) => {
  const [showModalSide, setShowModalSide] = useState(false);
  const [productIdSelected, setProductIdSelected] = useState<number | null>(null);
  const { productsModuleListingFilters, setProductsModuleListingFilters } = useFiltersState();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isNearBottom, setIsNearBottom] = useState(false);

  const debouncedSearch = useDebounce(searchText, 800);

  // Esta función retorna la key de cada página,
  // su valor será aceptado por `fetcher`.
  // Si `null` es retornado, la solicitud de esa página no comenzará pues se habrá llegado al final de la lista.
  const getKey = (pageIndex: number, previousPageData: IToolData[]) => {
    if (previousPageData && !previousPageData.length) return null;
    const searchParam = debouncedSearch ? `&search=${debouncedSearch}` : '';
    const filters = productsModuleListingFilters.reduce((acc: { [key: string]: string[] }, filter) => {
      acc[filter.name] = filter.values;
      return acc;
    }, {} as { [key: string]: string[] });
    const filtersParam = Object.keys(filters).length ? `&filters=${JSON.stringify(filters)}` : '';
    return `/backoffice-tools-view?page=${pageIndex}&limit=400&productType=stock${searchParam}${filtersParam}`;
  };

  const {
    data: pages = [],
    isLoading,
    isValidating,
    size,
    setSize,
    mutate: refreshToolsListing
  } = useSWRInfinite(getKey, getToolsListing, {
    revalidateFirstPage: false,
    revalidateOnFocus: false
  });

  const allProducts = pages.flat();

  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const threshold = 100; // píxeles antes del final
    const isNearingBottom = scrollHeight - scrollTop - clientHeight < threshold;

    setIsNearBottom(isNearingBottom);
  }, []);

  const setupScrollListener = () => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
    return undefined;
  };

  const handleModalSide = (productId: number) => {
    setShowModalSide(!showModalSide);
    setProductIdSelected(productId);
  };

  useEffect(() => {
    return setupScrollListener();
  }, [setupScrollListener]);

  useEffect(() => {
    if (isNearBottom && !isLoading && !isValidating && pages[pages.length - 1]?.length > 0) {
      setSize(size + 1);
    }
  }, [isNearBottom, isLoading, isValidating, pages, setSize, size]);

  useEffect(() => {
    return () => {
      setProductsModuleListingFilters([]);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={containerRef} className="productsStockListing">
      {showModalSide && productIdSelected && (
        <ProductDetailModal
          productIdSelected={productIdSelected}
          isOpen={showModalSide}
          onClose={() => setShowModalSide(false)}
          listOfCountries={listOfCountries || []}
          refreshToolsListing={refreshToolsListing}
        />
      )}
      {allProducts.map((product: IToolData) => (
        <ProductModuleListingCard
          key={product.id}
          product={product}
          listOfCountries={listOfCountries || []}
          handleModalSide={handleModalSide}
          listProductsType={LIST_PRODUCTS_TYPE.STOCK_BORD}
        />
      ))}
      {(isLoading || isValidating) &&
        Array.from({ length: 20 }, (_, index) => (
          <ProductsListCardSkeletonLoader key={index} listProductsType={LIST_PRODUCTS_TYPE.STOCK_BORD} />
        ))}
    </div>
  );
};

export default ProductsStockListing;
